/**
 * Dispositivi ricordati e codici di emergenza della 2FA: rotte del core (flusia-core, dominio twofa)
 * /api/twofa-trusted-device-login, /api/twofa-trusted-devices, /api/twofa-backup-codes, chiamate con
 * coreCall (sempre sul core: CORE_ONLY_ROUTES in services/gatewayService.ts, non esiste piu' la edge).
 * Stesso contratto delle edge che sostituiscono (17/10/2026).
 * Logica pura in ./trustedDeviceStorage.ts, ./emailCode.ts, ./codeInput.ts.
 */
import { supabase } from '../supabase';
import { coreCall } from '../services/gatewayService';
import { clearTrustedDeviceToken, readTrustedDeviceToken, saveTrustedDeviceToken } from './trustedDeviceStorage';

export interface TrustedDevice {
  id: string;
  user_agent: string | null;
  ip: string | null;
  created_at: string;
  expires_at: string;
  last_used_at: string | null;
}

async function currentSession() {
  const { data } = await supabase.auth.getSession();
  return data.session ?? null;
}

/** Messaggio italiano della funzione dal corpo della risposta d'errore (non 2xx), se c'e'. */
export async function readFunctionError(error: unknown, fallback = 'Errore di comunicazione con il server'): Promise<string> {
  let message = (error as { message?: string })?.message || fallback;
  try {
    const ctx = (error as { context?: Response }).context;
    const parsed = ctx && typeof ctx.json === 'function' ? await ctx.json() : null;
    if (parsed?.error) message = parsed.error;
  } catch {
    // messaggio generico
  }
  return message;
}

/** Rotta 2FA del core: errore HTTP o { error } nel corpo diventano un Error col messaggio italiano del server. */
async function invoke<T>(route: string, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await coreCall<T & { error?: string }>(route, { body });
  if (error) throw new Error(error.message || 'Errore di comunicazione con il server');
  if (data?.error) throw new Error(data.error);
  return data as T;
}

/**
 * Subito dopo la password: se questo browser e' un dispositivo ricordato dell'utente, la sessione
 * viene registrata come verificata e restituisce il token di sessione 2FA. Altrimenti null (passo del codice).
 */
export async function tryTrustedDeviceLogin(): Promise<string | null> {
  try {
    const session = await currentSession();
    const userId = session?.user?.id;
    if (!session || !userId) return null;
    const deviceToken = readTrustedDeviceToken(userId);
    if (!deviceToken) return null;
    const { data, error } = await coreCall<{ trusted?: boolean; twoFactorSessionToken?: string }>(
      'twofa-trusted-device-login',
      { body: { deviceToken } },
    );
    if (!error && data?.trusted && data.twoFactorSessionToken) return data.twoFactorSessionToken as string;
    // Rifiutato (revocato, scaduto, altro utente): non riprovare la prossima volta
    if (!error && data && data.trusted === false) clearTrustedDeviceToken(userId);
    return null;
  } catch (err) {
    console.warn('[2FA] Dispositivo ricordato non verificabile, si passa al codice:', err);
    return null;
  }
}

/** Salva il token restituito da twofa-verify-code quando l'utente ha scelto "Ricorda questo dispositivo". */
export async function rememberThisDevice(token: string, expiresAt: string): Promise<void> {
  const session = await currentSession();
  if (session?.user?.id) saveTrustedDeviceToken(session.user.id, token, expiresAt);
}

export async function listTrustedDevices(): Promise<TrustedDevice[]> {
  const data = await invoke<{ devices: TrustedDevice[] }>('twofa-trusted-devices', { action: 'list' });
  return data.devices || [];
}

export async function revokeTrustedDevice(deviceId: string): Promise<void> {
  await invoke('twofa-trusted-devices', { action: 'revoke', deviceId });
}

export async function revokeAllTrustedDevices(): Promise<void> {
  await invoke('twofa-trusted-devices', { action: 'revoke_all' });
  const session = await currentSession();
  if (session?.user?.id) clearTrustedDeviceToken(session.user.id);
}

export async function getBackupCodesRemaining(): Promise<number> {
  const data = await invoke<{ remaining: number }>('twofa-backup-codes', { action: 'status' });
  return data.remaining ?? 0;
}

export async function generateBackupCodes(): Promise<string[]> {
  const data = await invoke<{ codes: string[] }>('twofa-backup-codes', { action: 'generate' });
  return data.codes || [];
}
